import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Add, Edit, Delete, FilterAltOff } from "@mui/icons-material";
import { serviceSubCategoriesAPI, categoriesAPI } from "../../services/api";
import browserGlobal from "../../utils/browserGlobal";
import { clearFilterButtonSx } from "../../utils/clearFilterButtonSx";
import { useLanguage } from "../../context/LanguageContext";
import { getAdminUser } from "../../utils/authSessionStorage";
import { canManageContacts, canDeleteContacts } from "../../utils/roleUtils";

function getIsCancelled(opts) {
  if (typeof opts?.isCancelled === "function") return opts.isCancelled;
  return () => false;
}

function getApiTotalCount(apiTotal, fallbackLength) {
  if (apiTotal != null && Number(apiTotal) >= 0) return Number(apiTotal);
  return fallbackLength;
}

const ServiceSubCategories = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const canAddEdit = canManageContacts(adminUser?.role);
  const canDelete = canDeleteContacts(adminUser?.role);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: "", type: "info" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    categoryId: "",
    search: "",
  });

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.search || "").trim();
      const prev = debouncedSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.search]);

  const [formData, setFormData] = useState({
    name_EN: "",
    name_DE: "",
    categoryId: "",
    isEnabled: true,
  });

  const showToast = (text, type = "info") => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchCategories = useCallback(async (opts = {}) => {
    const isCancelled = getIsCancelled(opts);
    try {
      const response = await categoriesAPI.getCategories();
      if (isCancelled()) return;
      if (response?.data?.success) {
        setCategories(response.data.payload || []);
        if (response.data.payload?.length > 0) {
          setFilters((prev) => ({
            ...prev,
            categoryId: response.data.payload[0].id,
          }));
        }
      }
    } catch (error) {
      if (!isCancelled()) {
        console.error("Error fetching categories:", error);
      }
    }
  }, []);

  const fetchSubCategories = useCallback(
    async (opts = {}) => {
      const isCancelled = getIsCancelled(opts);
      if (!filters.categoryId) return;
      setIsLoading(true);
      try {
        const response = await serviceSubCategoriesAPI.getServiceSubCategories(
          filters.categoryId,
          page,
          rowsPerPage,
          { search: debouncedSearch },
        );
        if (isCancelled()) return;
        if (response?.data?.success) {
          const payload = response.data.payload || [];
          const total = getApiTotalCount(response?.data?.totalRecord, payload.length);
          setSubCategories(payload);
          setTotalCount(total);
        } else {
          setSubCategories([]);
          setTotalCount(0);
        }
      } catch (err) {
        if (isCancelled()) return;
        console.error("Error fetching sub categories:", err);
        setSubCategories([]);
        setTotalCount(0);
      } finally {
        if (!isCancelled()) {
          setIsLoading(false);
        }
      }
    },
    [filters.categoryId, page, rowsPerPage, debouncedSearch],
  );

  useEffect(() => {
    let cancelled = false;
    fetchCategories({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [fetchCategories]);

  useEffect(() => {
    let cancelled = false;
    fetchSubCategories({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [fetchSubCategories]);

  const visibleSubCategories = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const hay = (v) => String(v ?? "").toLowerCase();
    if (!q) return subCategories;
    return subCategories.filter(
      (sc) => hay(sc.name_EN).includes(q) || hay(sc.name_DE).includes(q),
    );
  }, [subCategories, debouncedSearch]);

  const handleClearFilters = () => {
    setFilters((prev) => ({ ...prev, search: "" }));
    setDebouncedSearch("");
    setPage(0);
  };

  const handleOpen = (subCat = null) => {
    if (subCat) {
      setSelectedSubCategory(subCat);
      setFormData({
        name_EN: subCat.name_EN || "",
        name_DE: subCat.name_DE || "",
        categoryId: subCat.categoryId || "",
        isEnabled: subCat.isEnabled === 1,
      });
    } else {
      setSelectedSubCategory(null);
      setFormData({
        name_EN: "",
        name_DE: "",
        categoryId: filters.categoryId || "",
        isEnabled: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedSubCategory(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // simple validation
    const payload = { ...formData, isEnabled: formData.isEnabled ? 1 : 0 };
    if (!formData.name_EN || !formData.name_DE || !formData.categoryId) {
      showToast(t("serviceSub.toastFill"), "warning");
      return;
    }
    setIsSubmitting(true);
    try {
      if (selectedSubCategory) {
        await serviceSubCategoriesAPI.updateServiceSubCategory({
          ...payload,
          id: selectedSubCategory.id,
        });
        showToast(t("serviceSub.toastUpdated"), "success");
      } else {
        await serviceSubCategoriesAPI.createServiceSubCategory(payload);
        showToast(t("serviceSub.toastCreated"), "success");
      }
      fetchSubCategories();
      handleClose();
    } catch (err) {
      console.error("Error saving sub category:", err);
      showToast(t("serviceSub.toastSaveError"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!browserGlobal.confirm(t("serviceSub.confirmDelete")))
      return;
    try {
      await serviceSubCategoriesAPI.deleteServiceSubCategory(id);
      showToast(t("serviceSub.toastDeleted"), "success");
      fetchSubCategories();
    } catch (err) {
      console.error("Error deleting sub category:", err);
      showToast(t("serviceSub.toastDeleteError"), "error");
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={2}
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">{t("serviceSub.title")}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: "linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)",
              color: "#ffffff",
              "&:hover": {
                background: "linear-gradient(135deg, #328284 0%, #0F3D3E 100%)",
              },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {t("serviceSub.add")}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "#fff",
          borderRadius: 1,
          boxShadow: "0 2px 4px rgba(124, 58, 237, 0.05)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("serviceSub.filterCategory")}</InputLabel>
              <Select
                value={filters.categoryId}
                label={t("serviceSub.filterCategory")}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, categoryId: e.target.value }));
                  setPage(0);
                }}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.title_EN}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder={t("serviceSub.searchPlaceholder")}
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: "flex", alignItems: "center", ml: { md: "auto" } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={clearFilterButtonSx}
            >
              {t("common.clear")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: "#0F3D3E" }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "0 2px 4px rgba(124, 58, 237, 0.05)",
            borderRadius: 2,
            "& .MuiTableCell-head": {
              backgroundColor: "#ebf4f4",
              color: "#0F3D3E",
              fontWeight: 600,
              padding: "16px",
            },
            "& .MuiTableCell-body": { padding: "16px" },
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("serviceSub.colEnglish")}</TableCell>
                <TableCell>{t("serviceSub.colGerman")}</TableCell>
                <TableCell>{t("serviceSub.colEnabled")}</TableCell>
                <TableCell>{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleSubCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    {t("serviceSub.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                visibleSubCategories.map((sc) => (
                  <TableRow
                    key={sc.id}
                    sx={{ "&:hover": { backgroundColor: "#ebf4f4" } }}
                  >
                    <TableCell>{sc.name_EN}</TableCell>
                    <TableCell>{sc.name_DE}</TableCell>
                    <TableCell>{sc.isEnabled ? t("common.yes") : t("common.no")}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        title={t("common.edit")}
                        onClick={() => handleOpen(sc)}
                      >
                        <Edit />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          color="error"
                          size="small"
                          title={t("common.delete")}
                          onClick={() => handleDelete(sc.id)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={t("common.rowsPerPage")}
          />
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedSubCategory ? t("serviceSub.dialogEdit") : t("serviceSub.dialogAdd")}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t("serviceSub.nameEn")}
            name="name_EN"
            fullWidth
            required
            value={formData.name_EN}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label={t("serviceSub.nameDe")}
            name="name_DE"
            fullWidth
            required
            value={formData.name_DE}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t("serviceSub.fieldCategory")}</InputLabel>
            <Select
              name="categoryId"
              label={t("serviceSub.fieldCategory")}
              value={formData.categoryId}
              onChange={handleChange}
              required
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.title_EN}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isEnabled: e.target.checked,
                  }))
                }
                color="primary"
              />
            }
            label={t("accommodation.fieldEnabled")}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              background: "linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)",
            }}
          >
            {isSubmitting ? t("common.saving") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: "100%" }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceSubCategories;
